import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { 
  Briefcase, 
  Users, 
  FileText, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  ExternalLink,
  Loader2
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CareerJobsDialog } from "@/components/admin/careers/CareerJobsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  listApplications, 
  updateApplicationStatus, 
  getResumeUrl,
  type ApplicationWithJob 
} from "@/lib/admin/careers.functions";

export const Route = createFileRoute("/_admin/admin/careers")({
  head: () => ({
    meta: [{ title: "Careers Management | Admin" }],
  }),
  component: CareersAdminPage,
});

function CareersAdminPage() {
  const queryClient = useQueryClient();
  const [jobsOpen, setJobsOpen] = useState(false);
  const fetchApps = useServerFn(listApplications);
  const updateStatus = useServerFn(updateApplicationStatus);
  const fetchResume = useServerFn(getResumeUrl);


  const { data: applications, isLoading } = useQuery({
    queryKey: ["admin", "careers", "applications"],
    queryFn: () => fetchApps(),
  });

  const statusMutation = useMutation({
    mutationFn: updateStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "careers", "applications"] });
      toast.success("Application status updated");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to update status");
    },
  });

  const handleStatusChange = (id: string, status: any) => {
    statusMutation.mutate({ data: { id, status } });
  };

  const handleDownloadResume = async (applicationId: string) => {
    try {
      const { signedUrl } = await fetchResume({ data: { applicationId } });
      window.open(signedUrl, "_blank");
    } catch {
      toast.error("Could not generate resume link");
    }
  };


  const prepareExportData = () => {
    if (!applications) return [];
    return applications.map(app => ({
      "Full Name": app.full_name,
      "Email": app.email,
      "Phone": app.phone,
      "Job Title": app.career_jobs?.title || "N/A",
      "Status": app.status,
      "Applied On": format(new Date(app.applied_at), "yyyy-MM-dd HH:mm"),
      "City": app.city || "",
      "Total Exp": app.total_experience || "",
      "Relevant Exp": app.relevant_experience || "",
      "Current Employer": app.current_employer || "",
      "Education": app.highest_qualification || ""
    }));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "selected":
        return <Badge className="bg-green-600 hover:bg-green-700">Selected</Badge>;
      case "rejected":
        return <Badge variant="destructive">Rejected</Badge>;
      case "shortlisted":
        return <Badge className="bg-blue-600 hover:bg-blue-700">Shortlisted</Badge>;
      case "interviewed":
      case "interview_scheduled":
        return <Badge className="bg-purple-600 hover:bg-purple-700">Interview</Badge>;
      case "resume_reviewed":
        return <Badge className="bg-cyan-600 hover:bg-cyan-700">Reviewed</Badge>;
      default:
        return <Badge variant="secondary">New</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-medium tracking-tight">Careers & Applications</h1>
          <p className="text-muted-foreground mt-1">Review candidate applications and manage job listings.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="rounded-full" onClick={() => setJobsOpen(true)}>
            <Briefcase className="mr-2 h-4 w-4" />
            Manage Jobs
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Applications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{applications?.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending Review</CardTitle>
          </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-orange-600">
            {applications?.filter(a => a.status === 'new').length || 0}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Shortlisted / Selected</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-600">
            {applications?.filter(a => ['shortlisted', 'selected'].includes(a.status)).length || 0}
          </div>
        </CardContent>
      </Card>
      </div>

      <Card>
        <div className="p-4 border-b">
          <AdminDataToolbar 
            exportName="CareerApplications"
            rows={prepareExportData}
            moduleId="career-applications"
            onImported={() => queryClient.invalidateQueries({ queryKey: ["admin", "careers", "applications"] })}
          />
        </div>
        <CardContent className="p-0">
          <div className="relative overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Applied For</TableHead>
                  <TableHead>Experience</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Applied On</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : applications?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                      No applications found.
                    </TableCell>
                  </TableRow>
                ) : (
                  applications?.map((app) => (
                    <TableRow key={app.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{app.full_name}</span>
                          <span className="text-xs text-muted-foreground">{app.email}</span>
                          <span className="text-xs text-muted-foreground">{app.phone}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>{app.career_jobs?.title}</span>
                          <span className="text-xs text-muted-foreground">{app.career_jobs?.location}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">
                          <div>Total: {app.total_experience || "N/A"}</div>
                          <div className="text-muted-foreground">Relevant: {app.relevant_experience || "N/A"}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={app.status} 
                          onValueChange={(val) => handleStatusChange(app.id, val)}
                          disabled={statusMutation.isPending}
                        >
                          <SelectTrigger className="h-8 w-[130px] text-xs">
                            <SelectValue>{getStatusBadge(app.status)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="resume_reviewed">Reviewed</SelectItem>
                            <SelectItem value="shortlisted">Shortlisted</SelectItem>
                            <SelectItem value="interview_scheduled">Interview Scheduled</SelectItem>
                            <SelectItem value="interviewed">Interviewed</SelectItem>
                            <SelectItem value="selected">Selected</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                            <SelectItem value="withdrawn">Withdrawn</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs">
                        {format(new Date(app.applied_at), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => handleDownloadResume(app.id)}
                            title="View Resume"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                            <a href={`mailto:${app.email}`} title="Email Candidate">
                              <Users className="h-4 w-4" />
                            </a>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <CareerJobsDialog open={jobsOpen} onOpenChange={setJobsOpen} />
    </div>
  );
}
